import { SYNC_FAILURE_SUBSCRIPTION } from 'api/graphql/v2/subscriptions/Events';
import React, { useEffect } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import { useLocation } from 'react-router';
import { toast } from 'react-toastify';
import type { Observable } from 'zen-observable-ts';
import { FEATURES, getFeatureAvailability } from 'utils/featureToggle';
import { limitProviderName } from 'utils/constants/toastMessage';
import {
  ROUTE_PARAMS,
  ROUTE_PATHS,
  TARGET_BLANK
} from 'utils/constants/routes';
import { isPlannerRoute } from 'helpers/routeHelper';
import { SyncNotice, isCentralReachDownStream } from './ToastUtility';
import ToastMessage from './ToastMessage';

// Minimal structural type — we only need unsubscribe().
type ZenSubscription = { unsubscribe: () => void };

const FAILURE_STATUS_MAPPINGS: Record<string, string> = {
  CREATE_FAILURE: 'Creation',
  UPDATE_FAILURE: 'Update',
  CANCEL_FAILURE: 'Cancellation'
};

const notify = (failureNotice: SyncNotice | null | undefined) => {
  if (!failureNotice) return;

  // Suppress Central Reach sync toasts when CR status is hidden.
  if (
    getFeatureAvailability(FEATURES.CR_SYNC_DISABLED) &&
    isCentralReachDownStream(failureNotice.downStream)
  ) {
    return;
  }

  const { eventId, downStream, isSupressLink } = failureNotice;
  const noticeId = `${eventId}-${downStream}`;
  const link = ROUTE_PATHS.VIEW_APPOINTMENT + ROUTE_PARAMS.EVENT + eventId;

  const onToastClick = () => {
    if (!isSupressLink) window.open(link, TARGET_BLANK);
  };

  toast.error(syncNoticeContent(failureNotice), {
    toastId: noticeId,
    containerId: noticeId,
    autoClose: false,
    closeOnClick: !isSupressLink,
    onClick: onToastClick
  });
};

const syncNoticeContent = (failureNotice: SyncNotice) => {
  const { providerName, downStream, eventId } = failureNotice;
  const time = new Date(failureNotice.startDate!).toLocaleTimeString(
    navigator.language,
    {
      hour: '2-digit',
      minute: '2-digit'
    }
  );
  const action = FAILURE_STATUS_MAPPINGS[failureNotice.status!];
  const message = `${limitProviderName(
    providerName!
  )}'s ${time} ${action} failed to sync to ${downStream}`;

  const onClose = (e: any) => {
    const toastId = `${eventId}-${downStream}`;
    e.stopPropagation();
    toast.dismiss(toastId);
  };

  return <ToastMessage message={message} onClose={onClose} />;
};
type ApolloLike = {
  subscribe: (options: { query: any; variables?: any }) => Observable<any>;
};

let refCount = 0;
let activeSub: ZenSubscription | null = null;
let activeCreatedBy: string | undefined;
let teardownTimer: ReturnType<typeof setTimeout> | null = null;

const startSubscription = (client: ApolloLike, createdBy: string) => {
  if (teardownTimer !== null) {
    clearTimeout(teardownTimer);
    teardownTimer = null;
  }
  if (activeSub && activeCreatedBy === createdBy) {
    return;
  }
  if (activeSub && activeCreatedBy !== createdBy) {
    activeSub.unsubscribe();
    activeSub = null;
  }

  console.log(
    '[SyncFailureToast] starting singleton subscription for CreatedBy:',
    createdBy
  );
  activeCreatedBy = createdBy;
  activeSub = client
    .subscribe({
      query: SYNC_FAILURE_SUBSCRIPTION,
      variables: { CreatedBy: createdBy }
    })
    .subscribe({
      next: ({
        data
      }: {
        data?: { eventSyncFailureNotice?: SyncNotice };
      }) => {
        const failureNotice = data?.eventSyncFailureNotice;
        if (!failureNotice) return;
        // No publish-time route gate needed — the subscription itself is
        // only alive while the user is on the Planner (see effect below).
        notify(failureNotice);
      },
      error: (err: any) => {
        console.error('[SyncFailureToast] subscription error:', err);
      }
    });
};

const scheduleTeardown = () => {
  if (teardownTimer !== null) return;
  teardownTimer = setTimeout(() => {
    teardownTimer = null;
    if (refCount > 0) return;
    console.log('[SyncFailureToast] tearing down singleton subscription');
    activeSub?.unsubscribe();
    activeSub = null;
    activeCreatedBy = undefined;
  }, 0);
};

interface Props {
  account: any;
}

const SyncFailureToast: React.FC<Props> = ({ account }: Props) => {
  const location = useLocation();
  const onPlannerPage = isPlannerRoute(location?.pathname);
  const client = (useApolloClient() as unknown) as ApolloLike;
  const createdBy: string | undefined = account?.name;

  useEffect(() => {
    if (!getFeatureAvailability(FEATURES.FAILURE_NOTICE_SUBSCRIPTIONS)) {
      return;
    }
    if (!createdBy) return;
    if (!onPlannerPage) return;

    refCount += 1;
    startSubscription(client, createdBy);
    return () => {
      refCount = Math.max(0, refCount - 1);
      if (refCount === 0) {
        scheduleTeardown();
      }
    };
  }, [client, createdBy, onPlannerPage]);

  return <></>;
};

export default SyncFailureToast;
